import { SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FormDatePicker } from '@/components/form/FormDatePicker';
import { FormReactSelect } from '@/components/form/FormReactSelect';
import { FormSection } from '@/components/form/FormSection';
import { Domain } from '@/features/domains';

import { ReportFormProps } from '../../types';
import { downloadReport } from '../../utils/downloadReport';
import { handleDownloadReportError } from '../../utils/handleDownloadReportError';
import { ReportModalForm } from '../ReportModalForm';
import { SchemasPicker } from '../SchemasPicker';

type RangeOption = {
  value: 'day' | 'month' | 'year' | 'range';
  label: string;
};

type Form = {
  rangeType: RangeOption | null;
  date: Date | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  schemas: Array<Domain>;
};

const RANGE_OPTIONS: RangeOption[] = [
  { value: 'day', label: 'Dzień' },
  { value: 'month', label: 'Miesiąc' },
  { value: 'year', label: 'Rok' },
  { value: 'range', label: 'Zakres dat' },
];

const INITIAL_VALUES: Form = {
  rangeType: RANGE_OPTIONS[0],
  date: new Date(),
  dateFrom: new Date(),
  dateTo: new Date(),
  schemas: [],
};

const schema = yup.object({
  rangeType: yup.object().nullable().required('Pole wymagane'),
  date: yup
    .date()
    .nullable()
    .when('rangeType', (rangeType, schema) =>
      rangeType?.value === 'range' ? schema : schema.required('Pole wymagane')
    ),
  dateFrom: yup
    .date()
    .nullable()
    .when('rangeType', (rangeType, schema) =>
      rangeType?.value === 'range' ? schema.required('Pole wymagane') : schema
    ),
  dateTo: yup
    .date()
    .nullable()
    .when('rangeType', (rangeType, schema) =>
      rangeType?.value === 'range'
        ? schema
            .required('Pole wymagane')
            .min(yup.ref('dateFrom'), 'Data do musi być późniejsza lub równa dacie od')
        : schema
    ),
  schemas: yup.array().min(1, 'Pole wymagane'),
});

export const ChildStaffAttendanceByBranchWarszawaForm = (props: ReportFormProps) => {
  const { report } = props;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm<Form>({
    defaultValues: INITIAL_VALUES,
    resolver: yupResolver(schema),
  });

  const rangeType = watch('rangeType');

  const handleFormSubmit = async (values: Form) => {
    try {
      const baseParams = {
        schemas: values.schemas.map((domain) => domain.domain),
      };

      const rangeValue = values.rangeType?.value;
      const params = (() => {
        switch (rangeValue) {
          case 'day':
            return {
              ...baseParams,
              date: dayjs(values.date).format('YYYY-MM-DD'),
            };
          case 'month':
            return {
              ...baseParams,
              month: dayjs(values.date).format('YYYY-MM'),
            };
          case 'year':
            return {
              ...baseParams,
              year: dayjs(values.date).format('YYYY'),
            };
          case 'range':
            return {
              ...baseParams,
              dateFrom: dayjs(values.dateFrom).format('YYYY-MM-DD'),
              dateTo: dayjs(values.dateTo).format('YYYY-MM-DD'),
            };
          default:
            return baseParams;
        }
      })();

      await downloadReport(report, params);
      props.onClose();
    } catch (err: any) {
      handleDownloadReportError(err);
    }
  };

  return (
    <ReportModalForm
      {...props}
      onSubmit={handleSubmit(handleFormSubmit)}
      isSubmitting={isSubmitting}
    >
      <FormSection title={'Placówki'}>
        <SchemasPicker control={control} />
      </FormSection>
      <FormSection title={'Zakres raportu'}>
        <FormReactSelect
          control={control}
          name={'rangeType'}
          options={RANGE_OPTIONS}
          getOptionLabel={(o) => o.label}
          getOptionValue={(o) => o.value}
          placeholder={'Wybierz zakres'}
        />
      </FormSection>
      {rangeType?.value === 'day' && (
        <FormSection title={'Na dzień'}>
          <FormDatePicker control={control} name={'date'} placeholderText={'Wybierz datę'} />
        </FormSection>
      )}
      {rangeType?.value === 'month' && (
        <FormSection title={'Miesiąc'}>
          <FormDatePicker
            control={control}
            name={'date'}
            placeholderText={'Wybierz miesiąc'}
            showMonthYearPicker
            dateFormat={'MM-yyyy'}
          />
        </FormSection>
      )}
      {rangeType?.value === 'year' && (
        <FormSection title={'Rok'}>
          <FormDatePicker
            control={control}
            name={'date'}
            placeholderText={'Wybierz rok'}
            showYearPicker
            dateFormat={'yyyy'}
          />
        </FormSection>
      )}
      {rangeType?.value === 'range' && (
        <FormSection title={'Zakres dat'}>
          <SimpleGrid columns={2} gap={4}>
            <Stack>
              <Text>Data od</Text>
              <FormDatePicker
                control={control}
                name={'dateFrom'}
                placeholderText={'Wybierz datę od'}
              />
            </Stack>
            <Stack>
              <Text>Data do</Text>
              <FormDatePicker
                control={control}
                name={'dateTo'}
                placeholderText={'Wybierz datę do'}
              />
            </Stack>
          </SimpleGrid>
        </FormSection>
      )}
    </ReportModalForm>
  );
};
